import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { fuseAnimations } from '@fuse/animations';
import { FuseCardComponent } from '@fuse/components/card';
import { FuseAlertComponent } from '@fuse/components/alert';
import { AIFormsService } from '../../ai-forms.service';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { Subject, takeUntil } from 'rxjs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { Options } from '@angular-slider/ngx-slider';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { ResultComponent } from '../result/result.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AuthSrcDirective } from 'app/modules/shared/directives/auth-src.directive';
import { TranslocoModule } from '@ngneat/transloco';


@Component({
  selector: 'app-view-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
        AuthSrcDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatDatepickerModule,
    NgxMaterialTimepickerModule,
    NgxSliderModule,
    MatSlideToggleModule,
    TranslocoModule
  ],
  templateUrl: './view-form.component.html',
  styleUrl: './view-form.component.scss'
})
export class ViewFormComponent implements OnInit, OnDestroy {
  formId: string | null = null;
  datasourceId: string | null = null;
  form: FormGroup = this.fb.group({});
  isLoading: boolean = false;
  dataLoaded: boolean = false;
  fields: any[] = [];
  formName: string = '';
  isFormEnabled: boolean = false;
  userData: { logo: string, slogan: string } | null = null;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private _formService: AIFormsService,
    private _alertService: CustomAlertService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.formId = params.get('formId');
      this.datasourceId = params.get('datasourceId');
      this.getFormDetails(this.formId, this.datasourceId);
    });
  }

  getFormDetails(formId: string | null, datasourceId: string | null): void {
    this.isLoading = true;
    this._formService
      .getFormDetails(formId, datasourceId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (res: any) => {
          this.isLoading = false;
          const { formName, fields, user, enabled } = res.data.data;

          this.formName = formName;
          this.isFormEnabled = enabled;
          this.fields = fields;
          this.userData = user;
          this.buildForm(fields);
          this.dataLoaded = true;
          this._changeDetectorRef.markForCheck();
        },
        (error) => {
          this.isLoading = false;
          this._alertService.failedMessage(error.error.message);
        }
      );
  }
  sharpnessConfig: Options = {
    floor: 0.0,
    ceil: 30.0,
  };
  sharpnessValue = 2.0;
  buildForm(fields: any[]): void {
    fields.forEach((field) => {
      const validators: any[] = [];
      if (field.componentStyle === 'slider') {
        const sliderConfig: Options = {
          floor: field.extras.sliderMinValue ?? 0,
          ceil: field.extras.sliderMaxValue ?? 100,
        };

        validators.push(
          field.isRequired ? Validators.required : null,
          Validators.min(sliderConfig.floor),
          Validators.max(sliderConfig.ceil)
        );

        this.form.addControl(
          field.fieldName,
          this.fb.control(field.extras.sliderDefaultValue || 0, validators.filter(Boolean))
        );

        field['sliderConfig'] = sliderConfig;
      } else if (field.componentStyle === 'dropdown') {
        validators.push(field.isRequired ? Validators.required : null);

        this.form.addControl(
          field.fieldName,
          this.fb.control(field.extras && field.extras.defaultValue || null, validators.filter(Boolean))
        );
      } else if (field.componentStyle === 'input' && field.type === 'string') {
        validators.push(
          field.isRequired ? Validators.required : null,
          field.extras && field.extras.maxChars ? Validators.maxLength(field.extras.maxChars) : null
        );

        this.form.addControl(field.fieldName, this.fb.control('', validators.filter(Boolean)));
      } else if (field.componentStyle === 'input' && field.type === 'number') {
        validators.push(
          field.isRequired ? Validators.required : null,
          field.extras && field.extras.minValue !== undefined ? Validators.minLength(field.extras.minValue) : null,
          field.extras && field.extras.maxValue !== undefined ? Validators.maxLength(field.extras.maxValue) : null
        );

        this.form.addControl(field.fieldName, this.fb.control(null, validators.filter(Boolean)));
      } else if (field.componentStyle === 'textarea') {
        validators.push(
          field.isRequired ? Validators.required : null,
          field.extras && field.extras.maxCharsTextArea
            ? Validators.maxLength(field.extras.maxCharsTextArea)
            : null
        );

        this.form.addControl(field.fieldName, this.fb.control('', validators.filter(Boolean)));
      } else if (field.componentStyle === 'input' && field.type === 'date') {
        validators.push(field.isRequired ? Validators.required : null);
        this.form.addControl(field.fieldName, this.fb.control(null, validators.filter(Boolean)));
      }
      else {
        this.form.addControl(field.fieldName, this.fb.control('', validators));
      }

      // Change detection
      this._changeDetectorRef.markForCheck();
    });
  }

  onSubmit(): void {
    if (this.form.valid) {
      const modal = {
        dataSourceId: this.datasourceId,
        formId: this.formId,
        data: this.form.value
      }

      this._formService.addDataToForm(modal).pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
        this.displayResult('success', 'Data submitted successfully')
        this._changeDetectorRef.markForCheck();
      }, (error) => {
        this.displayResult('error', error.error.message);
      })
    } else {
    }
  }

  displayResult(type, message) {
    const dialogRef = this._dialog.open(ResultComponent, {
      width: '25%',
      panelClass: 'cropperDialog',
      data: {
        type: type,
        message: message
      },
    });
    dialogRef.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      this.resetForm();
    });
  }

  resetForm(): void {
    // Reset the form values
    this.form.reset();

    // Reset the state of all controls to pristine and untouched
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.markAsPristine();
      control?.markAsUntouched();
    });

    // If there are additional dynamic configurations (e.g., sliders), reset them
    this.fields.forEach((field) => {
      if (field.componentStyle === 'slider') {
        this.form.get(field.fieldName)?.setValue(field.sliderDefaultValue || field.sliderConfig.floor || 0);
      }
    });

    // Trigger change detection to update the UI
    this._changeDetectorRef.markForCheck();
  }


  trackByField(index: number, field: any): string {
    return field.fieldName;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
